// Seek: one search over the whole archive. Realms are known at once; the ages, events and people of every
// chronicle come from one small index (data/chronicles/search.json), fetched the first time Seek opens. A year
// typed on its own ("1453", "356 BC") is a place too. Keyboard first: `/` or ⌘K opens it, the arrows choose,
// Enter goes, Esc closes. The panel opens at its final size and never changes it: results scroll inside it.

import { fmtYear, parseYear } from "@hoee/chart";
import type { Chart } from "@hoee/chart";
import type { SearchIndex } from "@hoee/schema";

export type ItemType = "realm" | "age" | "event" | "person" | "year";

/** Where choosing a result takes you. */
export type Target =
  | { type: "realm"; realm: string }
  | { type: "age"; realm: string; age: string }
  | { type: "event"; realm: string; id: string }
  | { type: "person"; realm: string; id: string }
  | { type: "year"; year: number };

/** One thing Seek can find. */
export interface Item {
  type: ItemType;
  title: string;
  meta: string;
  /** 1 (most) to 3. */
  importance: number;
  /** Folded text the query is matched against. */
  hay: string;
  /** The realm's pigment, for its chip; -1 for none. */
  color: number;
  target: Target;
  /** For realms, the years they lived; for events, their year. */
  from?: number;
  to?: number;
}

const TYPE: Record<ItemType, string> = { realm: "Realm", age: "Age", event: "Event", person: "Life", year: "Year" };
const WEIGHT: Record<ItemType, number> = { realm: 14, person: 11, age: 8, event: 6, year: 0 };

/** Lower case without accents, so "Đại Việt" is found by "dai viet". */
export const fold = (s: string): string =>
  s.normalize("NFD").replace(/\p{M}/gu, "").replace(/[đĐ]/g, "d").toLowerCase();

const span = (from: number, to: number, now: number, before = false) =>
  `${before ? "before " : ""}${fmtYear(from)} – ${to >= now ? "today" : fmtYear(to)}`;

/** Every realm on the chart, findable before anything else has loaded. */
export function realmItems(chart: Chart, now = new Date().getFullYear()): Item[] {
  return chart.realms.map((r) => {
    const lands = r.lanes.map((l) => chart.columns[l]?.name ?? "");
    return {
      type: "realm",
      title: r.name,
      meta: `${span(r.start, r.end, now, r.fades)} · ${lands.slice(0, 3).join(", ")}${lands.length > 3 ? "…" : ""}`,
      importance: 1,
      hay: fold(`${r.name} ${r.tagline} ${lands.join(" ")}`),
      color: r.color,
      target: { type: "realm", realm: r.id },
      from: r.start,
      to: r.end,
    };
  });
}

/** The ages, events and people of every chronicle, from the search index. */
export function indexItems(chart: Chart, ix: SearchIndex): Item[] {
  const items: Item[] = [];
  const realms = ix.realms.map((id) => chart.realmById.get(id));
  const ages = ix.ages.map(([r, parent, id, name, from, to]) => ({ r, parent, id, name, from, to }));
  /** An age and the ages it sits in, outermost first. */
  const chain = (a: number) => {
    const out: (typeof ages)[number][] = [];
    for (let age = ages[a]; age; age = ages[age.parent]) out.unshift(age);
    return out;
  };
  const where = (realm: string, a: number) => [realm, ...chain(a).map((x) => x.name)].join(" › ");
  for (const age of ages) {
    const realm = realms[age.r];
    if (!realm) continue;
    items.push({
      type: "age",
      title: age.name,
      meta: `${fmtYear(age.from)} – ${fmtYear(age.to)} · ${where(realm.name, age.parent)}`,
      importance: 2,
      hay: fold(`${age.name} ${realm.name}`),
      color: realm.color,
      target: { type: "age", realm: realm.id, age: age.id },
    });
  }
  for (const [r, a, id, title, year, approx, importance, , place, names] of ix.events) {
    const realm = realms[r];
    if (!realm) continue;
    const up = chain(a);
    items.push({
      type: "event",
      title,
      meta: `${approx ? "c. " : ""}${fmtYear(year)} · ${where(realm.name, a)}`,
      importance,
      hay: fold(`${title} ${names} ${place} ${realm.name} ${up.map((x) => x.name).join(" ")}`),
      color: realm.color,
      target: { type: "event", realm: realm.id, id },
      from: year,
      to: year,
    });
  }
  for (const [r, id, name, born, died, floruit, role, importance] of ix.people) {
    const realm = realms[r];
    if (!realm) continue;
    const years =
      born != null || died != null
        ? `${born != null ? fmtYear(born) : "?"} – ${died != null ? fmtYear(died) : "?"}`
        : `active ${fmtYear(floruit ?? 0)}`;
    items.push({
      type: "person",
      title: name,
      meta: `${years} · ${role} · ${realm.name}`,
      importance,
      hay: fold(`${name} ${role} ${realm.name}`),
      color: realm.color,
      target: { type: "person", realm: realm.id, id },
    });
  }
  return items;
}

/**
 * The results for a query, best first. Words found in a title count most, then the kind of thing (a realm
 * before a life before an age before an event) and its importance. A year on its own is offered first, with
 * the events of that year and the realms alive in it.
 */
export function search(items: readonly Item[], query: string, range: [number, number], limit = 40): Item[] {
  const q = fold(query.trim());
  if (!q) {
    // Some famous moments to start from, spread across time.
    const famous = items.filter((it) => it.type === "event" && it.importance === 1);
    famous.sort((a, b) => (a.from ?? 0) - (b.from ?? 0));
    const step = Math.max(1, Math.floor(famous.length / 8));
    return famous.filter((_, i) => i % step === 0).slice(0, 8);
  }
  const out: { it: Item; score: number }[] = [];
  const year = parseYear(query);
  if (year != null) {
    if (year >= range[0] && year <= range[1])
      out.push({
        it: {
          type: "year",
          title: fmtYear(year),
          meta: "Go to this year: every land says who ruled it",
          importance: 1,
          hay: "",
          color: -1,
          target: { type: "year", year },
        },
        score: 1e6,
      });
    for (const it of items) {
      if (it.type === "event" && it.from === year) out.push({ it, score: 5e5 - it.importance });
      else if (it.type === "realm" && (it.from ?? Infinity) <= year && year < (it.to ?? -Infinity))
        out.push({ it, score: 1e5 - it.title.length });
    }
  } else {
    const tokens = q.split(/\s+/).filter(Boolean);
    for (const it of items) {
      if (!tokens.every((t) => it.hay.includes(t))) continue;
      const title = fold(it.title);
      let score = WEIGHT[it.type] + (4 - it.importance) * 4 - title.length * 0.05;
      score += tokens.filter((t) => title.includes(t)).length * 18;
      if (title === q) score += 80;
      else if (title.startsWith(q)) score += 50;
      else if (title.split(/[\s-]+/).some((w) => w.startsWith(tokens[0] ?? ""))) score += 24;
      else if (title.includes(q)) score += 12;
      out.push({ it, score });
    }
  }
  const seen = new Set<string>();
  return out
    .sort((a, b) => b.score - a.score)
    .map((o) => o.it)
    .filter((it) => {
      const key = `${it.type}|${it.title}|${it.meta}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, limit);
}

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls: string, text?: string) => {
  const node = document.createElement(tag);
  node.className = cls;
  if (text) node.textContent = text;
  return node;
};

export class Seek {
  readonly dialog: HTMLDialogElement;
  private readonly input: HTMLInputElement;
  private readonly list: HTMLUListElement;
  private readonly status: HTMLElement;
  private readonly empty: HTMLElement;
  private readonly chart: Chart;
  private readonly onChoose: (target: Target) => void;
  private items: Item[];
  private loaded: Promise<void> | null = null;
  private ready = false;
  private shown: Item[] = [];
  private active = 0;

  constructor(chart: Chart, onChoose: (target: Target) => void) {
    this.chart = chart;
    this.onChoose = onChoose;
    this.items = realmItems(chart);

    this.dialog = el("dialog", "seek");
    this.dialog.setAttribute("aria-label", "Seek");
    const head = el("div", "seek-head");
    const glyph = el("span", "seek-glyph", "✦");
    glyph.setAttribute("aria-hidden", "true");
    this.input = el("input", "seek-input");
    Object.assign(this.input, {
      type: "text",
      placeholder: "A realm, an age, a person, an event or a year",
      autocomplete: "off",
      spellcheck: false,
      enterKeyHint: "go",
    });
    this.input.setAttribute("role", "combobox");
    this.input.setAttribute("aria-label", "Seek a realm, an age, a person, an event or a year");
    this.input.setAttribute("aria-autocomplete", "list");
    this.input.setAttribute("aria-expanded", "true");
    this.input.setAttribute("aria-controls", "seek-list");
    const esc = el("button", "seek-esc", "esc");
    esc.type = "button";
    esc.setAttribute("aria-label", "Close");
    esc.addEventListener("click", () => this.close());
    head.append(glyph, this.input, esc);

    this.list = el("ul", "seek-list");
    this.list.id = "seek-list";
    this.list.setAttribute("role", "listbox");
    this.list.setAttribute("aria-label", "Results");
    this.empty = el("p", "seek-empty");
    this.empty.setAttribute("aria-hidden", "true");
    const foot = el("div", "seek-foot");
    for (const [key, what] of [
      ["↑ ↓", "choose"],
      ["↵", "go"],
      ["esc", "close"],
    ]) {
      const hint = el("span", "seek-key");
      hint.setAttribute("aria-hidden", "true");
      hint.append(el("kbd", "", key), ` ${what}`);
      foot.append(hint);
    }
    this.status = el("p", "seek-status");
    this.status.setAttribute("role", "status");
    foot.append(this.status);
    const body = el("div", "seek-body");
    body.append(this.list, this.empty);
    this.dialog.append(head, body, foot);

    this.input.addEventListener("input", () => {
      this.active = 0;
      this.render();
    });
    this.input.addEventListener("keydown", (ev) => this.key(ev));
    // Esc (the dialog's own cancel) closes with the same fade as everything else.
    this.dialog.addEventListener("cancel", (ev) => {
      ev.preventDefault();
      this.close();
    });
    // A click on the backdrop, outside the panel, closes it.
    this.dialog.addEventListener("pointerdown", (ev) => {
      if (ev.target === this.dialog) {
        const r = this.dialog.getBoundingClientRect();
        if (ev.clientX < r.left || ev.clientX > r.right || ev.clientY < r.top || ev.clientY > r.bottom) this.close();
      }
    });
    window.addEventListener("keydown", (ev) => {
      const typing = ev.target instanceof HTMLInputElement || ev.target instanceof HTMLTextAreaElement;
      if ((ev.metaKey || ev.ctrlKey) && !ev.altKey && ev.key.toLowerCase() === "k") {
        ev.preventDefault();
        if (this.dialog.open) this.close();
        else this.open();
      } else if (ev.key === "/" && !typing && !this.dialog.open) {
        ev.preventDefault();
        this.open();
      }
    });
  }

  get isOpen(): boolean {
    return this.dialog.open;
  }

  open(): void {
    if (this.dialog.open) return;
    this.input.value = "";
    this.active = 0;
    this.dialog.classList.remove("open");
    this.dialog.showModal();
    this.input.focus();
    requestAnimationFrame(() => this.dialog.classList.add("open"));
    this.render();
    void this.load().then(() => this.dialog.open && this.render());
  }

  close(): void {
    if (!this.dialog.open) return;
    this.dialog.classList.remove("open");
    setTimeout(() => {
      if (!this.dialog.classList.contains("open")) this.dialog.close();
    }, 160);
  }

  private load(): Promise<void> {
    this.loaded ??= fetch("data/chronicles/search.json")
      .then((r) => (r.ok ? (r.json() as Promise<SearchIndex>) : null))
      .catch(() => null)
      .then((ix) => {
        if (ix) this.items = [...this.items, ...indexItems(this.chart, ix)];
        this.ready = true;
      });
    return this.loaded;
  }

  private key(ev: KeyboardEvent): void {
    const n = this.shown.length;
    if (ev.key === "ArrowDown" || ev.key === "ArrowUp") {
      ev.preventDefault();
      if (n) this.setActive((this.active + (ev.key === "ArrowDown" ? 1 : n - 1)) % n);
    } else if (ev.key === "PageDown" || ev.key === "PageUp") {
      ev.preventDefault();
      if (n) this.setActive(Math.max(0, Math.min(n - 1, this.active + (ev.key === "PageDown" ? 8 : -8))));
    } else if (ev.key === "Enter") {
      ev.preventDefault();
      const it = this.shown[this.active];
      if (it) this.choose(it);
    } else if (ev.key === "Escape") {
      ev.preventDefault();
      this.close();
    }
  }

  private choose(it: Item): void {
    this.close();
    this.onChoose(it.target);
  }

  private render(): void {
    const query = this.input.value;
    this.shown = search(this.items, query, [this.chart.axis.start, this.chart.axis.end]);
    this.active = Math.min(this.active, Math.max(0, this.shown.length - 1));
    const rows = this.shown.map((it, i) => {
      const li = el("li", `seek-row t-${it.type}`);
      li.id = `seek-opt-${i}`;
      li.setAttribute("role", "option");
      const chip = el("span", "seek-chip", it.type === "year" ? "⌛" : "");
      chip.setAttribute("aria-hidden", "true");
      if (it.color >= 0) chip.dataset.p = String(it.color);
      const text = el("span", "seek-text");
      text.append(el("span", "seek-title", it.title), el("span", "seek-meta", it.meta));
      const type = el("span", "seek-type", TYPE[it.type]);
      li.append(chip, text, type);
      li.addEventListener("pointermove", () => {
        if (this.active !== i) this.setActive(i, false);
      });
      li.addEventListener("click", () => this.choose(it));
      return li;
    });
    if (!query.trim() && rows.length) {
      const head = el("li", "seek-group", "Some places to start");
      head.setAttribute("role", "presentation");
      rows.unshift(head);
    }
    this.list.replaceChildren(...rows);
    this.list.scrollTop = 0;
    const none =
      this.ready || parseYear(query) != null ? "Nothing in the archive matches that yet." : "Opening the archive…";
    this.empty.textContent = this.shown.length ? "" : none;
    this.status.textContent = this.shown.length
      ? query.trim()
        ? `${this.shown.length === 40 ? "40 or more" : this.shown.length} found`
        : ""
      : none;
    this.setActive(this.active);
  }

  private setActive(i: number, scroll = true): void {
    this.active = i;
    this.list.querySelectorAll<HTMLElement>("[role=option]").forEach((row, k) => {
      row.classList.toggle("active", k === i);
      row.setAttribute("aria-selected", String(k === i));
      if (k === i && scroll) row.scrollIntoView({ block: "nearest" });
    });
    if (this.shown.length) this.input.setAttribute("aria-activedescendant", `seek-opt-${i}`);
    else this.input.removeAttribute("aria-activedescendant");
  }
}
